import { useState, useEffect, useRef, useId, cloneElement, isValidElement } from 'react';
import { TOOLTIP_BG, TOOLTIP_TEXT, FONT_FAMILY_DISPLAY } from '../../theme';

const SHOW_DELAY_MS = 200;
const GAP_PX = 6;
const ESTIMATED_HEIGHT_PX = 60;

const wrapperSt = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
};

const tooltipBaseSt = {
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  background: TOOLTIP_BG,
  color: TOOLTIP_TEXT,
  borderRadius: 6,
  padding: '0.4rem 0.6rem',
  fontFamily: FONT_FAMILY_DISPLAY,
  fontSize: '0.78rem',
  fontWeight: 400,
  lineHeight: 1.4,
  maxWidth: 260,
  width: 'max-content',
  whiteSpace: 'normal',
  textTransform: 'none',
  letterSpacing: 'normal',
  boxShadow: '0 2px 8px rgba(15, 34, 53, 0.12)',
  zIndex: 50,
  pointerEvents: 'none',
};

export default function Tooltip({ text, children, position = 'top', disabled = false }) {
  const [visible, setVisible]               = useState(false);
  const [actualPosition, setActualPosition] = useState(position);
  const wrapperRef                          = useRef(null);
  const showTimerRef                        = useRef(null);
  const reactId                             = useId();
  const tooltipId                           = `tooltip-${reactId}`;

  useEffect(() => () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
  }, []);

  if (disabled || !isValidElement(children)) {
    return children ?? null;
  }

  const show = () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    showTimerRef.current = setTimeout(() => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (rect && position === 'top' && rect.top < ESTIMATED_HEIGHT_PX) {
        setActualPosition('bottom');
      } else {
        setActualPosition(position);
      }
      setVisible(true);
    }, SHOW_DELAY_MS);
  };

  const hide = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    setVisible(false);
  };

  const trigger = cloneElement(children, {
    'aria-describedby': visible ? tooltipId : undefined,
    onMouseEnter: (e) => { children.props.onMouseEnter?.(e); show(); },
    onMouseLeave: (e) => { children.props.onMouseLeave?.(e); hide(); },
    onFocus:      (e) => { children.props.onFocus?.(e);      show(); },
    onBlur:       (e) => { children.props.onBlur?.(e);       hide(); },
  });

  const positionSt = actualPosition === 'top'
    ? { bottom: `calc(100% + ${GAP_PX}px)` }
    : { top:    `calc(100% + ${GAP_PX}px)` };

  return (
    <span ref={wrapperRef} style={wrapperSt}>
      {trigger}
      {visible && (
        <span id={tooltipId} role="tooltip" style={{ ...tooltipBaseSt, ...positionSt }}>
          {text}
        </span>
      )}
    </span>
  );
}
